import { MatchedPlaidTransaction, extractDate } from "@ecountabl/lib"
import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import type { StackNavigationProp } from "@react-navigation/stack"
import api from '../api-client'
import { ScrollView, SectionList, Text, TouchableOpacity, View } from "react-native"
import Loader from "../utility/loader"
import Transaction from "./transaction"
import { useNavigation } from "@react-navigation/native"
import { ThemeContext } from "../utility/context/ThemeContext"
import { MAX_CONTENT_WIDTH } from "../config"
import { textStyles } from "../style"
import { CustomActionSheet, CustomActionSheetRef } from "../utility/CustomActionSheet"
import AddCompanyForm from "../add-company-form"
import ThemedDialog from "../style/dialog"
import { Ionicons, MaterialIcons } from "@expo/vector-icons"
import { useAppSelector } from "../store"
import { usePlaidLink } from "../utility/context"
import { TransactionsSkeleton } from "./transactions-skeleton"

type Props = {
  route: {params?: {
    searchTerm?: string
    sort?: string
    filter?: string
    txType?: "all" | "income" | "expense"
    values?: string | string[]
  }}
}

type TransactionStackParamList = {
  TransactionsHome: Props['route']['params'];
  CompanySearchResults: { term?: string; sort?: string };
  Company: { company?: { name?: string }; name?: string };
}

type TransactionNavigationProp = StackNavigationProp<TransactionStackParamList>

type TransactionGroup = {
  date: string;
  transactions: MatchedPlaidTransaction[];
}

type AddBrandOptions = {
  isLocal?: boolean;
  tx?: MatchedPlaidTransaction;
}

const transactionSortLabelMap:Record<string, string> = {
  "date new": "Newest to Oldest",
  "date old": "Oldest to Newest",
  "amount high": "Amount High to Low",
  "amount low": "Amount Low to High",
}

const TransactionsByDate:React.FC = ({route}: Props) => {
  const navigation = useNavigation<TransactionNavigationProp>()

  const { accounts, institutions } = useAppSelector((state) => state.accountsSlice)
  const { map: valuesMap } = useAppSelector(state => state.values)

  const { startPlaidLinkSession } = usePlaidLink()

  const theme = useContext(ThemeContext)
  const { bgMain, primary, textMain, textSecond } = theme.theme.colors

  const [transactions, setTransactions] = useState<TransactionGroup[]>([])
  const [page, setPage] = useState(0)
  const [canFetchMore, setCanFetchMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [addBrandOpts, setAddBrandOpts] = useState<AddBrandOptions | null>(null)
  
  const actionSheetRef = useRef<CustomActionSheetRef>(null)
  const isFetchingRef = useRef(false)
  const latestRequestIdRef = useRef(0)

  const transactionsPerFetch = 20
  const today = new Date()
  const yesterday = new Date(today.getTime() - (24 * 60 * 60 * 1000))

  const normalizeParamArray = (param?: string | string[]) => {
    if (!param) return []
    return (Array.isArray(param) ? param : [param])
      .map(v => `${v}`.trim())
      .filter(Boolean)
  }
  const getValueBubbleLabel = (valueSlugOrMode: string): string => {
    if (valueSlugOrMode === "any") return "Any"
    if (valueSlugOrMode === "none") return "None"
    return valuesMap?.[valueSlugOrMode]?.label || valueSlugOrMode
  }
  const getValueBubbleBgColor = (valueSlugOrMode: string): string | undefined => {
    if (valueSlugOrMode === "any" || valueSlugOrMode === "none") {
      return undefined
    }
    const valueRecord = (
      valuesMap?.[valueSlugOrMode] as unknown as Record<string, unknown> | undefined
    )
    const valueColor = (
      typeof valueRecord?.color === "string"
        ? valueRecord.color
        : typeof valueRecord?.hexColor === "string"
          ? valueRecord.hexColor
          : undefined
    )
    return valueColor ? `${valueColor}40` : undefined
  }
  const getSortBubbleLabel = (sortKey?: string): string => {
    if (!sortKey) return ""
    return transactionSortLabelMap[sortKey] || sortKey
  }

  const attemptGetTransactions = useCallback(async (
    page: number,
    numTransactions: number,
    searchTerm?: string,
    sort?: string,
    filter?: string,
    options?: {
      txType?: "all" | "income" | "expense";
      values?: string | string[];
    }
  ): Promise<{
    transactions: {date: string, transactions: MatchedPlaidTransaction[]}[], canFetchMore: boolean
  }> => {
    try {
      // First attempt to get transactions
      return await api.getTransactionsByDate(page, numTransactions, searchTerm, sort, filter, {
        txType: options?.txType,
        values: options?.values,
      })
    } catch (e) {
      if (e.status === 400 && e.tryAgain) {
        await new Promise((resolve) => setTimeout(resolve, 3000)) // Wait for 3 seconds
  
        // Retry fetching transactions by recursively calling attemptGetTransactions
        return attemptGetTransactions(page, numTransactions, searchTerm, sort, filter, options)
      } else {
        // If error is not retryable, rethrow the error
        throw e
      }
    }
  }, [])

  const mergeTransactions = useCallback((
    oldTransactions: {date: string, transactions: MatchedPlaidTransaction[]}[],
    newTransactions: {date: string, transactions: MatchedPlaidTransaction[]}[]
  ) => {
    if (newTransactions.length === 0) {
      return oldTransactions
    }

    let index = 0
    // merge the new transactions with the existing ones in reverse order
    if (oldTransactions[oldTransactions.length - 1].date === newTransactions[index].date) {
      // we are merging the same date, so we need to merge the transactions
      oldTransactions[oldTransactions.length - 1].transactions = [
        ...oldTransactions[oldTransactions.length - 1].transactions,
        ...newTransactions[0].transactions
      ]
      index += 1
    }

    // add the rest of the transactions
    for (let i = index; i < newTransactions.length; i++) {
      oldTransactions.push(newTransactions[i])
    }

    return oldTransactions
  }, [])

  const fetchTransactions = useCallback(async (
    requestedPage: number,
    opts?: { reset?: boolean }
  ) => {
    const reset = !!opts?.reset
    if (isFetchingRef.current && !reset) return

    const requestId = ++latestRequestIdRef.current
    isFetchingRef.current = true

    setLoading(true)
    setError(null)

    if (reset) {
      setTransactions([])
      setCanFetchMore(true)
      setPage(0)
    }

    try {
      const transactionData = await attemptGetTransactions(
        requestedPage,
        transactionsPerFetch,
        route?.params?.searchTerm || "",
        route?.params?.sort || "date new",
        route?.params?.filter,
        {
          txType: route?.params?.txType || "all",
          values: route?.params?.values,
        }
      )

      if (requestId !== latestRequestIdRef.current) return

      setCanFetchMore(transactionData.canFetchMore)
      setPage(requestedPage)
      setTransactions((prev) => {
        if (reset) return transactionData.transactions
        return mergeTransactions([...prev], transactionData.transactions)
      })
    } catch (e) {
      if (requestId !== latestRequestIdRef.current) return
      setCanFetchMore(false)
      setError(e?.message || "An error occurred while fetching transactions")
      if (reset) {
        setTransactions([])
      }
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false)
        isFetchingRef.current = false
      }
    }
  }, [
    attemptGetTransactions,
    mergeTransactions,
    route?.params?.searchTerm,
    route?.params?.sort,
    route?.params?.filter,
    route?.params?.txType,
    route?.params?.values
  ])

  useEffect(() => {
    // Fetch as soon as the screen renders and whenever search/sort/filter changes.
    // Do not gate on accounts/institutions hydration; the API can return an empty list.
    fetchTransactions(0, { reset: true })
  }, [
    fetchTransactions,
    // if accounts or institutions change, we need to refetch from page 0
    accounts.length,
    institutions.length
  ])

  const toggleAddBrand = (show: boolean, opts?:AddBrandOptions) => {
    setAddBrandOpts(opts || null)

    if (show) {
      actionSheetRef.current?.show()
    } else {
      actionSheetRef.current?.hide()
    }
  }

  const renderDateString = (date: string) => {
    const dateObj = extractDate(date)

    if (dateObj.toDateString() === today.toDateString()) {
      return "Today"
    } else if (dateObj.toDateString() === yesterday.toDateString()) {
      return "Yesterday"
    } else {
      return dateObj.toLocaleDateString()
    }
  }

  const renderSectionHeader = ({ section }: { section: { title: string } }) => (
    <View
      key={section.title}
      style={{
        backgroundColor: bgMain,
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderRadius: 8
      }}
    >
      <Text
        style={[
          textStyles.h4,
          {color: textMain}
        ]}
      >
        {renderDateString(section.title)}
      </Text>
    </View>
    
  )

  const renderItem = ({ item }: { item: MatchedPlaidTransaction }) => (
    <View style={{paddingHorizontal: 16}}>
    <Transaction
      tx={item}
      navigation={navigation}
      showDate={false}
      toggleAddBrand={toggleAddBrand}
      onErrorNavigating={() => setError("company-nav")}
    />
    </View>
  )

  const activeFilterBubbles = () => {
    const bubbles:{label: string; onClear: () => void; bgColor?: string}[] = []
    const params = route?.params || {}
    const values = normalizeParamArray(params.values)

    if (params.searchTerm) {
      bubbles.push({
        label: `Search: ${params.searchTerm}`,
        onClear: () => navigation.setParams({ searchTerm: "" })
      })
    }
    if (params.sort && params.sort !== "date new") {
      bubbles.push({
        label: `Sort: ${getSortBubbleLabel(params.sort)}`,
        onClear: () => navigation.setParams({ sort: "date new" })
      })
    }
    if (params.txType && params.txType !== "all") {
      const txTypeLabel = (
        params.txType === "income"
          ? "Income"
          : params.txType === "expense"
            ? "Expense"
            : null
      )
      if (txTypeLabel) {
        bubbles.push({
          label: `Filter: ${txTypeLabel}`,
          onClear: () => navigation.setParams({ txType: "all" })
        })
      }
    }
    if (values.length > 0) {
      values.forEach((value) => {
        bubbles.push({
          label: `Value: ${getValueBubbleLabel(value)}`,
          bgColor: getValueBubbleBgColor(value),
          onClear: () => {
            const nextValues = values.filter(v => v !== value)
            navigation.setParams({
              values: nextValues.length > 0 ? nextValues : undefined,
              // If no specific values remain, remove legacy aligned-filter fallback.
              filter: nextValues.length > 0 ? params.filter : undefined
            })
          }
        })
      })
    } else if (params.filter && params.filter !== "any") {
      bubbles.push({
        label: params.filter === "not-values" ? "Not Values Aligned" : "Values Aligned",
        onClear: () => navigation.setParams({
          filter: undefined,
          values: undefined,
         })
      })
    }
    if (bubbles.length === 0) {
      return null
    }

    return (
      <View style={{ paddingTop: 6, paddingBottom: 4 }}>
        {
          bubbles.length > 2 && (
            <View style={{paddingHorizontal: 16, paddingVertical: 8}}>
              <TouchableOpacity onPress={() => navigation.setParams({
                searchTerm: "",
                sort: "date new",
                txType: "all",
                values: undefined,
                filter: undefined,
              })}>
                <Text style={[textStyles.label, {color: textSecond}]}>
                  Clear All Filters
                </Text>
              </TouchableOpacity>
            </View>
          )
        }
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{gap: 6, alignItems: "center", paddingHorizontal: 16}}
        >
          {bubbles.map((bubble, index) => (
            <TouchableOpacity
              key={`${bubble.label}-${index}`}
              style={{
                flexDirection: "row",
                alignItems: "center",
                alignSelf: "flex-start",
                paddingHorizontal: 8,
                paddingVertical: 5,
                borderRadius: 100,
                backgroundColor: bubble.bgColor || `${primary}40`
              }}
              onPress={bubble.onClear}
            >
              <Text style={[textStyles.label, {color: textMain}]}>
                {bubble.label}{bubble.onClear ? " ×" : ""}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    )
  }

  const renderLoadingUI = () => {
    return (
      <View style={{ flex: 1, backgroundColor: bgMain}}>
        <View style={{width: "100%", maxWidth: MAX_CONTENT_WIDTH, alignSelf: "center"}}>
          <TransactionsSkeleton
            numberOfDates={5}
            transactionsPerDate={3}
            iconSize={50}
            valueDotSize={18}
          />
        </View>
      </View>
    )
  }

  const renderEmptyUI = () => {
    return (
      <View style={{flex: 1, justifyContent: "center", alignItems: "center", padding: 16, gap: 8}}>
        <View style={{alignSelf: "center", width: 100, aspectRatio: 1, backgroundColor: `${primary}4D`, borderRadius: 100, justifyContent: "center", alignItems: "center"}}>
          <MaterialIcons name="search-off" size={48} color={textMain}/>
        </View>
        
        <Text style={[textStyles.body, {textAlign: "center", color: textMain}]}>
          No transactions found{route?.params ? ". Consider modifying your search to find some transactions." : ""}
        </Text>
        {
          accounts.length === 0 && institutions.length === 0 ? (
            <Text style={[textStyles.body, {textAlign: "center", color: textMain}]}>
              No accounts linked? To get the most out of Impact Banking{'\n'}
              <Text
                onPress={() => {
                  startPlaidLinkSession({
                    redirectPath: "transactions",
                  })
                }}

                style={{fontWeight: "bold", color: primary}}
              >
                link an account
              </Text>
            </Text>
          ) : null
        }
      </View>
    )
  }

  const renderTransactionsUI = () => {
    return (
      <SectionList
        sections={transactions.map(t => ({ title: t.date, data: t.transactions }))}
        keyExtractor={(item , index: number) => item?._id?.toString() ?? `tx-${index}`}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        stickySectionHeadersEnabled
        contentContainerStyle={{alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH}}
        ItemSeparatorComponent={() => <View style={{height: 16}}/>}
        SectionSeparatorComponent={() => <View style={{height: 4}}/>}
        onEndReached={
          canFetchMore && !loading
            ? () => fetchTransactions(page + 1)
            : undefined
        }
        onEndReachedThreshold={0.4}
        ListFooterComponent={() => (
          loading ? (
            <View style={{paddingVertical: 5, alignItems: "center"}}>
              <Loader size="large"/>
            </View>
          ) : null
        )}
      />
    )
  }

  const closeErrorModal = () => {
    setError(null)
    if (transactions.length === 0) {
      navigation.goBack()
    }
  }

  return (
    <View style={{flex: 1, backgroundColor: bgMain}}>
      {activeFilterBubbles()}
      {
        transactions.length === 0 && loading ? renderLoadingUI() : null
      }
      {
        transactions.length === 0 && !loading ? renderEmptyUI() : null
      }
      {
        transactions.length > 0 ? renderTransactionsUI() : null
      }
      <CustomActionSheet
        ref={actionSheetRef}
        name="transactions-add-company"
      >
        <AddCompanyForm
          closeSheet={() => actionSheetRef.current?.hide()}
          isLocal={addBrandOpts?.isLocal}
          tx={addBrandOpts?.tx}
        />
      </CustomActionSheet>
      {/* ERROR MODAL */}
      <ThemedDialog
        title={error === "company-nav" ? "Error" : "There was an error gathering your transactions"}
        subMessage={error === "company-nav" ? "An error occurred while retrieving company information. Please try again later." : error}
        options={[
          {
            title: "Okay",
            role: "cancel",
            onPress: closeErrorModal,
            icon: <Ionicons name="checkmark" size={22}/>
          }
        ]}
        visible={!!error}
        errorAnimation={!!error}
      />
    </View>
  )
}

export default TransactionsByDate